import React, { type ChangeEvent, useCallback, useState } from "react";
import Spinner from "react-bootstrap/Spinner";

import type { FileModel } from "@admin/domain/files/model/FileModel";
import { makePath } from "@admin/utils/makePath";
import { uploadFile } from "@admin/domain/files/api";

type Props = {
    name: string;
    label: string;
    defaultValue?: FileModel[];
    accept?: string;
    multiple?: boolean;
    onChange?: (files: FileModel[]) => void;
};

export default function FileInput({
    name,
    label,
    defaultValue = [],
    accept,
    multiple = false,
    onChange,
}: Props) {
    const [files, setFiles] = useState<FileModel[]>(defaultValue);
    const [isUploading, setIsUploading] = useState<boolean>(false);

    const handleSelectedFile = async (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files;

        if (!selectedFiles || selectedFiles.length === 0) {
            return;
        }

        setIsUploading(true);

        const uploadedFiles: FileModel[] = [];

        for (const pendingFile of Array.from(selectedFiles)) {
            const file = await uploadFile(pendingFile);
            uploadedFiles.push(file);
        }

        const nextFiles = multiple ? [...files, ...uploadedFiles] : uploadedFiles;

        setFiles(nextFiles);
        onChange?.(nextFiles);

        setIsUploading(false);
        event.target.value = ``;
    };

    const remove = useCallback(
        (fileId: number) => {
            if (!confirm(`Вы действительно хотите удалить файл?`)) {
                return;
            }

            const nextFiles = files.filter((file) => file.id !== fileId);

            setFiles(nextFiles);
            onChange?.(nextFiles);
        },
        [files, onChange],
    );

    return (
        <div className="mb-5 position-relative">
            <label className="form-label">{label}</label>

            {files && files.length > 0 && (
                <>
                    {files.map((file, index) => (
                        <input
                            key={file.id}
                            type="hidden"
                            name={`${name}[${index}][file_id]`}
                            defaultValue={file.id}
                        />
                    ))}
                    <div className="border p-4 rounded mb-3">
                        {files.map((file) => (
                            <div key={file.id} className="position-relative mb-3">
                                <div>
                                    <span className="fw-bolder">Название файла:</span> {file.name}
                                </div>

                                <div>
                                    <span className="fw-bolder">Формат файла:</span> {file.mime}
                                </div>

                                <a
                                    href={makePath(file.path)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="fw-bolder"
                                >
                                    Скачать файл
                                </a>

                                <div className="position-absolute top-0 end-0">
                                    <i
                                        title="Удалить"
                                        className="fa fa-lg fa-trash text-danger cursor-pointer"
                                        onClick={() => remove(file.id)}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            <input
                type="file"
                className="form-control"
                accept={accept}
                multiple={multiple}
                onChange={handleSelectedFile}
            />

            {isUploading && (
                <div className="align-items-center bg-info bg-opacity-10 d-flex h-100 justify-content-center position-absolute start-0 top-0 w-100">
                    <Spinner variant="info" />
                </div>
            )}
        </div>
    );
}
